import { Link } from 'react-router-dom'

export default function DeleteAccountPage() {
  return (
    <div className="legal-doc min-h-screen bg-white py-10">
      <div className="max-w-3xl mx-auto px-6">

        {/* Header */}
        <div className="mb-8 pb-6 border-b-2 border-brand-text">
          <p className="text-xs uppercase tracking-widest text-brand-text-muted mb-2">
            RegenerAI — Documento Legal
          </p>
          <h1 className="text-3xl font-bold text-brand-text mb-2">
            Excluir minha conta no RegenerAI
          </h1>
          <p className="text-sm text-brand-text-secondary">
            Última atualização: 1º de maio de 2026
          </p>
        </div>

        {/* Preamble */}
        <p className="text-sm text-brand-text-secondary mb-8 leading-relaxed border-l-4 border-brand-border pl-4">
          Esta página descreve como solicitar a exclusão da sua conta e dos seus dados pessoais
          no RegenerAI, em conformidade com a Lei Geral de Proteção de Dados
          (LGPD — Lei nº 13.709/2018).
        </p>

        {/* Section 1 — How to delete in app */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-brand-text mb-3">
            1. Como excluir pelo aplicativo
          </h2>
          <p className="text-brand-text-secondary leading-relaxed mb-3">
            Se você tem acesso à sua conta, o caminho mais rápido é excluir diretamente
            pelo app:
          </p>
          <ol className="list-decimal list-inside space-y-1 text-brand-text-secondary leading-relaxed pl-2">
            <li>Abra o RegenerAI no celular ou em <a href="https://app.regenerai.ag" className="text-brand-primary underline">app.regenerai.ag</a></li>
            <li>Faça login com seu email e senha</li>
            <li>Acesse <strong>Configurações</strong></li>
            <li>Role até o final da página, na seção <strong>Excluir minha conta</strong></li>
            <li>Confirme com sua senha e clique em <strong>Excluir conta</strong></li>
          </ol>
          <p className="text-brand-text-secondary leading-relaxed mt-3">
            A exclusão é processada automaticamente após a confirmação.
          </p>
        </section>

        {/* Section 2 — What gets deleted */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-brand-text mb-3">
            2. O que será excluído
          </h2>
          <p className="text-brand-text-secondary leading-relaxed mb-3">
            Ao excluir sua conta, removemos os seguintes dados pessoais:
          </p>
          <ul className="list-disc list-inside space-y-1 text-brand-text-secondary leading-relaxed pl-2">
            <li>Nome, email, telefone e demais dados cadastrais do perfil</li>
            <li>Foto de perfil e preferências pessoais</li>
            <li>Histórico de conversas com o assistente de IA</li>
            <li>Conversas salvas, favoritos e marcações</li>
            <li>Comentários e curtidas em posts do blog</li>
            <li>Sessões ativas e tokens de autenticação</li>
          </ul>
        </section>

        {/* Section 3 — What may be retained */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-brand-text mb-3">
            3. O que pode ser retido por obrigação legal
          </h2>
          <p className="text-brand-text-secondary leading-relaxed mb-3">
            Conforme permitido pelo Art. 16 da LGPD, alguns dados podem ser mantidos por
            tempo limitado para cumprimento de obrigações legais, regulatórias ou de
            segurança:
          </p>
          <ul className="list-disc list-inside space-y-1 text-brand-text-secondary leading-relaxed pl-2">
            <li><strong>Hash anônimo do CPF</strong> — para impedir reabertura abusiva de contas previamente excluídas. Não permite identificação reversa.</li>
            <li><strong>Logs de segurança</strong> — registros de acesso e auditoria mantidos por até 6 meses, em conformidade com o Marco Civil da Internet (Lei nº 12.965/2014).</li>
            <li><strong>Dados fiscais e contratuais</strong> — caso aplicável a transações realizadas, retidos pelo prazo legal exigido (geralmente 5 anos).</li>
          </ul>
          <p className="text-brand-text-secondary leading-relaxed mt-3">
            Nenhum desses dados é utilizado para finalidades comerciais ou de marketing
            após a exclusão da conta.
          </p>
        </section>

        {/* Section 4 — Cannot access account */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-brand-text mb-3">
            4. Não consegue acessar sua conta?
          </h2>
          <p className="text-brand-text-secondary leading-relaxed mb-3">
            Se você perdeu o acesso ao seu email ou não consegue fazer login,
            envie uma solicitação para{' '}
            <a href="mailto:syntropy@gaasbrasil.com.br" className="text-brand-primary underline font-medium">
              syntropy@gaasbrasil.com.br
            </a>{' '}
            com as seguintes informações:
          </p>
          <ul className="list-disc list-inside space-y-1 text-brand-text-secondary leading-relaxed pl-2">
            <li>Assunto: <em>"Solicitação de exclusão de conta"</em></li>
            <li>Nome completo cadastrado</li>
            <li>Email utilizado no cadastro</li>
            <li>Cópia digital de documento oficial com foto (RG, CNH ou passaporte) — para confirmação de identidade e prevenção a fraudes</li>
          </ul>
          <p className="text-brand-text-secondary leading-relaxed mt-3">
            Trataremos seus documentos de forma confidencial e os descartaremos assim
            que a verificação for concluída.
          </p>
        </section>

        {/* Section 5 — Timeline */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-brand-text mb-3">
            5. Prazo para processamento
          </h2>
          <p className="text-brand-text-secondary leading-relaxed">
            Solicitações realizadas pelo aplicativo são processadas <strong>imediatamente</strong>.
            Solicitações enviadas por email serão processadas em até <strong>30 dias úteis</strong>{' '}
            a partir da confirmação da sua identidade, conforme prazo previsto pela LGPD.
          </p>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-brand-border">
          <p className="text-xs text-brand-text-muted mb-6 leading-relaxed">
            Este documento foi elaborado em conformidade com a Lei nº 13.709/2018 (LGPD)
            e demais normas expedidas pela Autoridade Nacional de Proteção de Dados (ANPD).
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/privacy" className="btn-secondary text-center">
              Política de Privacidade
            </Link>
            <Link to="/terms" className="btn-secondary text-center">
              Termos de Uso
            </Link>
          </div>
        </div>

      </div>
    </div>
  )
}
